import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ROLE_LABELS } from "./navigation/menu";
import { initials, searchMenu } from "./navigation/menuUtils";

const SHORTCUT_LABEL = /Mac|iPhone|iPad/.test(navigator.platform) ? "\u2318K" : "Ctrl K";

const Badge = ({ value }) => (value > 0 ? <span className="erp-nav__badge">{value > 99 ? "99+" : value}</span> : null);

const Sidebar = ({ menu, loading, active, badges, user, role, onRequestOpen, onClose }) => {
  const [query, setQuery] = useState("");
  const [openGroup, setOpenGroup] = useState(active?.group?.id ?? null);
  const searchRef = useRef(null);
  const scrollRef = useRef(null);

  const activeLeafId = active?.leaf.id;
  const activeGroupId = active?.group?.id;

  // Follow the route: the section holding the current page is the open one.
  useEffect(() => {
    if (activeGroupId) setOpenGroup(activeGroupId);
  }, [activeGroupId]);

  useEffect(() => {
    const current = scrollRef.current?.querySelector('[aria-current="page"]');
    current?.scrollIntoView({ block: "nearest" });
  }, [activeLeafId, loading]);

  useEffect(() => {
    const onKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        onRequestOpen();
        searchRef.current?.focus();
        searchRef.current?.select();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [onRequestOpen]);

  const searching = query.trim() !== "";
  const visibleMenu = useMemo(() => searchMenu(menu, query), [menu, query]);

  const onSearchKeyDown = (e) => {
    if (e.key === "Escape") {
      e.stopPropagation();
      setQuery("");
    }
    if (e.key === "Enter") {
      const first = scrollRef.current?.querySelector(".erp-nav__sublink, a.erp-nav__link");
      first?.click();
    }
  };

  const onNavigate = () => {
    setQuery("");
    onClose();
  };

  const renderLeaf = (item) => (
    <li key={item.id} className="erp-nav__item">
      <Link
        to={item.to}
        className={`erp-nav__link${item.id === activeLeafId ? " is-active" : ""}`}
        aria-current={item.id === activeLeafId ? "page" : undefined}
        title={item.label}
        onClick={onNavigate}
      >
        <i className={`fa ${item.icon} erp-nav__icon`} aria-hidden="true" />
        <span className="erp-nav__label">{item.label}</span>
        <Badge value={badges[item.badge]} />
      </Link>
    </li>
  );

  const renderGroup = (item) => {
    const expanded = searching || openGroup === item.id;
    const containsActive = item.id === activeGroupId;
    const panelId = `erp-nav-${item.id}`;

    return (
      <li key={item.id} className={`erp-nav__item erp-nav__group${expanded ? " is-open" : ""}`}>
        <button
          type="button"
          className={`erp-nav__link${containsActive ? " has-active" : ""}`}
          aria-expanded={expanded}
          aria-controls={panelId}
          title={item.label}
          onClick={() => setOpenGroup(expanded && !searching ? null : item.id)}
        >
          <i className={`fa ${item.icon} erp-nav__icon`} aria-hidden="true" />
          <span className="erp-nav__label">{item.label}</span>
          <Badge value={badges[item.badge]} />
          <i className="fa fa-angle-right erp-nav__chevron" aria-hidden="true" />
        </button>
        <div className="erp-nav__panel" id={panelId}>
          <ul className="erp-nav__sub">
            {item.children.map((child) => (
              <li key={child.id}>
                <Link
                  to={child.to}
                  className={`erp-nav__sublink${child.id === activeLeafId ? " is-active" : ""}`}
                  aria-current={child.id === activeLeafId ? "page" : undefined}
                  tabIndex={expanded ? undefined : -1}
                  onClick={onNavigate}
                >
                  <span>{child.label}</span>
                  <Badge value={badges[child.badge]} />
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </li>
    );
  };

  return (
    <>
      <aside className="erp-sidebar" aria-label="Main navigation">
        <div className="erp-sidebar__search">
          <i className="fa fa-search" aria-hidden="true" />
          <input
            ref={searchRef}
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onSearchKeyDown}
            placeholder="Search menu"
            aria-label="Search menu"
          />
          <kbd aria-hidden="true">{SHORTCUT_LABEL}</kbd>
        </div>

        <nav className="erp-sidebar__scroll" ref={scrollRef}>
          {loading && (
            <div className="erp-nav__skeleton" aria-hidden="true">
              {Array.from({ length: 8 }, (_, i) => (
                <span key={i} />
              ))}
            </div>
          )}

          {!loading && visibleMenu.length === 0 && (
            <p className="erp-nav__empty">{searching ? `No menu items match “${query.trim()}”.` : "No modules are assigned to your account."}</p>
          )}

          {visibleMenu.map((section) => (
            <div key={section.id} className="erp-nav__section">
              {section.heading && <div className="erp-nav__heading">{section.heading}</div>}
              <ul className="erp-nav">{section.items.map((item) => (item.children ? renderGroup(item) : renderLeaf(item)))}</ul>
            </div>
          ))}
        </nav>

        <div className="erp-sidebar__footer">
          <span className="erp-avatar erp-avatar--sm" aria-hidden="true">
            {initials(user?.name)}
          </span>
          <div className="erp-sidebar__user">
            <strong>{user?.name || " "}</strong>
            <span>{ROLE_LABELS[role] || user?.designation?.name || " "}</span>
          </div>
        </div>
      </aside>
      <div className="erp-backdrop" onClick={onClose} aria-hidden="true" />
    </>
  );
};

export default Sidebar;
